import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import {
  useTranslation,
  DropdownButton,
  Icon,
  DebouncedSearchInput,
  InfiniteScrollV2,
  DropdownItem,
  Empty,
} from '@testsigma/atoms';
import noResultFound from '@images/no-result-found.svg';
import { useFetchVersions } from '../../services/version.service';
import { ApplicationVersion } from '../../models/application-version.model';
import { Project } from '../../models/project.model';
import { Application } from '../../models/application.model';
import classNames from 'classnames';
import { UserPreferenceContext } from '@contexts/user-preference-context';
import { ListItemSkeleton } from '@modules/test-development/components/list-item-skeleton';

const { VirtualizedInfiniteLoader } = InfiniteScrollV2;

type VersionAutoCompleteProps = {
  project?: Project;
  selectedItem: ApplicationVersion | null;
  application: Application | null;
  updateSelected: (version: ApplicationVersion) => void;
  className?: string;
  placement?: string;
  label?: string;
  setIsFetchingVersions?: (isFetching: boolean) => void;
  dropdownClassName?: string;
  filterVersions?: (item: ApplicationVersion) => boolean;
};

const VersionAutoComplete: React.FC<VersionAutoCompleteProps> = ({
  selectedItem,
  application,
  updateSelected,
  className,
  placement,
  label = '',
  setIsFetchingVersions = () => {},
  dropdownClassName = '',
  filterVersions = () => true,
}) => {
  const { t } = useTranslation();
  const { userPreference } = useContext(UserPreferenceContext) || {};
  const projectPreference = userPreference?.preference?.projectPreference;

  const [value, setValue] = useState<string>('');
  const [isOpen, updateIsOpen] = useState<boolean>(false);
  const [versions, setVersions] = useState<ApplicationVersion[]>([]);

  const filterQuery = useMemo(
    () =>
      `applicationId:${application?.id}${
        value ? `,versionName:*${value}*` : ''
      }`,
    [application?.id, value],
  );

  const { data, fetchNextPage, hasNextPage, isFetching, isFetchingNextPage } =
    useFetchVersions({
      filterQuery,
      queryKey: [application?.id, value],
      enabled: !!application?.id,
      keepPreviousData: !value,
    });

  useEffect(() => {
    const { versionId, applicationId, selectedVersion } = projectPreference;
    let mergedContent = data?.pages?.flatMap((page) => page.content) || [];

    if (!!value) {
      return setVersions(mergedContent);
    }

    if (applicationId !== application?.id) {
      setVersions(mergedContent);
      updateSelected(mergedContent[0]);
      return;
    }

    let preferredVersion = mergedContent.find(
      (v: ApplicationVersion) => v.id === versionId,
    );

    if (!preferredVersion && selectedVersion?.id === versionId) {
      mergedContent = [selectedVersion, ...mergedContent];
      preferredVersion = selectedVersion;
    }

    setVersions(mergedContent);

    if (versionId !== selectedVersion?.id) {
      updateSelected(preferredVersion || mergedContent[0]);
    }
  }, [data]);

  useEffect(() => {
    setIsFetchingVersions(isFetching);
  }, [isFetching]);

  useEffect(() => {
    if (!application?.id) {
      setVersions([]);
      updateSelected(null);
    }
  }, [application?.id]);

  const filteredVersions = useMemo(() => {
    return versions.filter(filterVersions);
  }, [versions, filterVersions]);

  const rowRenderer = useCallback(
    (index: number) => {
      const item = filteredVersions[index];
      const isSelected = item?.id === selectedItem?.id;
      return (
        <DropdownItem
          key={item?.id}
          onClick={() => {
            updateSelected(item);
            updateIsOpen(false);
          }}
          className='flex items-center text-unique-600 hover:!bg-secondary-50 hover:!text-unique-600'
          isSelected={isSelected}
        >
          <div className='flex w-full items-center justify-between'>
            <div className='truncate w-[180px]' title={item?.name}>
              {item?.name}
            </div>
            {isSelected && (
              <Icon
                name='check-circle'
                className='text-primary-500 text-base'
              />
            )}
          </div>
        </DropdownItem>
      );
    },
    [filteredVersions, selectedItem, updateSelected],
  );

  return (
    <DropdownButton
      name={
        <div className='flex items-center'>
          <Icon className='text-[18px] pr-md text-unique-800' name='versions' />
          {selectedItem?.name}
        </div>
      }
      label={label || t('project_switcher.version')}
      variant='select-fill'
      placement={placement || 'bottom'}
      dropdownClassName={classNames(
        '!min-h-[240px] !max-h-[240px] !overflow-hidden',
        dropdownClassName,
      )}
      wrapperClassName={classNames('mb-[18px]', className)}
      labelClassName='mb-1'
      onClick={() => setValue('')}
      isOpen={isOpen}
      hideOnChildClick={false}
      setIsOpen={updateIsOpen}
    >
      <div>
        <DebouncedSearchInput
          value={value}
          onChange={setValue}
          placeholder={t('common.search')}
          wrapperClassName='outline-none w-full bg-white p-md sticky top-0 z-10'
        />
        {isFetching && !isFetchingNextPage ? (
          <ListItemSkeleton />
        ) : filteredVersions.length ? (
          <VirtualizedInfiniteLoader
            rowHeight={35}
            listHeight={175}
            rowRenderer={rowRenderer}
            rowCount={filteredVersions.length}
            totalPages={data?.pages?.[0]?.totalPages ?? 0}
            items={filteredVersions}
            loadMore={fetchNextPage}
            hasMore={hasNextPage}
            isLoading={isFetching}
            LoadingComponent={<ListItemSkeleton />}
          />
        ) : (
          <Empty
            description={<span>{t('no.results.found')}</span>}
            image={
              <img
                src={noResultFound}
                alt='empty-state-logo'
                className='mb-3'
              />
            }
            imageSource={''}
          />
        )}
      </div>
    </DropdownButton>
  );
};

export default VersionAutoComplete;
